import React, { useCallback, useEffect } from "react"

import { useObservable, useObservableState } from "observable-hooks"
import { distinctUntilChanged, EMPTY, map, switchMap } from "rxjs"

import { clamp } from "@treefort/lib/clamp"
import { Result } from "@treefort/lib/result"

import { AsyncViewOfflineProvider } from "../../components/async-view-offline-provider"
import { useActiveProfileId } from "../../hooks/use-active-profile-id"
import { useAsyncViewPropsForQueries } from "../../hooks/use-async-view-props-for-queries"
import useContent from "../../hooks/use-content"
import { useRouteParams } from "../../hooks/use-route-params"
import { getProgressItemFromProgressData } from "../../lib/progress-item"
import { progressStore } from "../../watermelon/stores/progress"
import { syncManager } from "../../watermelon/sync"
import { ModuleLayout } from "../layouts/module"
import { CardListViewModule } from "../layouts/module/list-view-modules/card"
import { VideoSeriesListViewModule } from "../layouts/module/list-view-modules/video-series"

export function ContentVideoSeriesScreen(): JSX.Element {
  const profileId = useActiveProfileId()
  const [params, setParams] = useRouteParams()

  // Track the user's selected season in a query param. If the user hasn't
  // selected a season, then the season is calculated based on their progress
  const selectedSeasonIndex =
    !params.season || isNaN(parseInt(params.season || "1"))
      ? undefined
      : Math.max(parseInt(params.season || "1") - 1, 0)
  const onChangeSeasonIndex = useCallback(
    (index: number) => setParams({ season: (index + 1).toString() }),
    [setParams],
  )

  if (!params.contentId) {
    throw new Error(`Visited the video series screen without a content id!`)
  }

  const contentId = parseInt(params.contentId)

  const videoSeriesQuery = useContent(contentId, "videoSeries")

  // Observe progress for video series episodes, with more recently played
  // episodes sorted first
  const videoSeriesProgress = useObservableState(
    useObservable(
      (observable) =>
        observable.pipe(
          switchMap(([profileId, videoSeries]) =>
            videoSeries
              ? progressStore
                  .observeAllByCurrentUser({
                    profileId,
                    orderBy: ["updatedAtDate", "desc"],
                  })
                  .pipe(
                    map((progress) =>
                      progress.flatMap((progress) => {
                        const progressItem = getProgressItemFromProgressData({
                          data: JSON.parse(progress.data),
                          profileId,
                        })
                        return progressItem?.getData().contentType ===
                          "video" &&
                          videoSeries.details.seasons.some((season) =>
                            season.episodes.some(
                              (episode) =>
                                episode.id ===
                                progressItem?.getData().contentId,
                            ),
                          )
                          ? {
                              progressItem,
                              progressItemUpdatedAt: progress.updatedAtDate,
                            }
                          : []
                      }),
                    ),
                    // Only update progress state if the most recently played
                    // item is different
                    distinctUntilChanged((prev, next) => {
                      const prevItem = prev[0]?.progressItem
                      const nextItem = next[0]?.progressItem

                      return (
                        prevItem?.getProgress().finished ===
                          nextItem?.getProgress().finished &&
                        prevItem?.getData().contentId ===
                          nextItem?.getData().contentId
                      )
                    }),
                  )
              : EMPTY,
          ),
        ),
      [profileId, videoSeriesQuery.data],
    ),
  )

  let defaultSeasonIndex = 0
  const listViewModules = Result.all(
    [
      videoSeriesQuery,
      videoSeriesProgress
        ? Result.success(videoSeriesProgress)
        : Result.loading(),
    ] as const,
    ([videoSeries, videoSeriesProgress]) => {
      const allEpisodes = videoSeries.details.seasons.flatMap(
        (season) => season.episodes,
      )
      const defaultEpisodeIndex = allEpisodes.findIndex(
        (episode) =>
          episode.id ===
          videoSeries.details.seasons.slice(-1)[0].episodes[0].id,
      )
      const lastPlayed = videoSeriesProgress[0]?.progressItem.getData()
      const lastPlayedIndex = allEpisodes.findIndex(
        (episode) => episode.id === lastPlayed?.contentId,
      )
      const currentEpisodeIndex =
        lastPlayedIndex < 0
          ? defaultEpisodeIndex < 0
            ? 0
            : defaultEpisodeIndex
          : lastPlayed.progress?.finished
            ? (lastPlayedIndex + 1) % allEpisodes.length
            : lastPlayedIndex
      const currentEpisode = allEpisodes[currentEpisodeIndex]
      defaultSeasonIndex = videoSeries.details.seasons.findIndex((season) =>
        season.episodes.some((episode) => episode.id === currentEpisode.id),
      )
      const seasonIndex = clamp(
        typeof selectedSeasonIndex === "number"
          ? selectedSeasonIndex
          : defaultSeasonIndex,
        0,
        videoSeries.details.seasons.length - 1,
      )

      return Result.success([
        new VideoSeriesListViewModule(
          {
            id: 0,
            type: "videoSeries",
            config: {
              contentId,
              preferredLayout: videoSeries.details.preferredLayout,
            },
            conditions: {},
          },
          {
            seasonIndex,
            onChangeSeasonIndex,
            currentEpisode,
            isSeriesStarted: Boolean(lastPlayed),
            videoSeries,
          },
        ),
        ...(videoSeries.links.length
          ? [
              new CardListViewModule({
                id: -1,
                type: "card",
                config: { items: videoSeries.links },
                conditions: {},
              }),
            ]
          : []),
      ])
    },
  )

  const asyncViewProps = useAsyncViewPropsForQueries(videoSeriesQuery)

  // Remounting the module when defaultSeasonIndex changes prevents layout
  // issues on mobile if the change happens while the user is on a different
  // screen and the new season has fewer episodes than the previous one
  const key = `video-series-${contentId}-default-season-${defaultSeasonIndex}`

  useEffect(() => {
    syncManager.requestSync({ syncType: "user-initiated" })
  }, [])

  return (
    <AsyncViewOfflineProvider key={key} {...asyncViewProps}>
      <ModuleLayout pageKey={key} listViewModules={listViewModules} />
    </AsyncViewOfflineProvider>
  )
}
